/**
 * Pages.js - Configuración centralizada de todas las rutas de la aplicación
 *
 * Este archivo gestiona:
 * 1. PublicPages: Rutas públicas sin autenticación (login, recuperar PIN, etc.)
 * 2. PrivatePages: Rutas privadas que requieren usuario autenticado (dashboard, perfil, etc.)
 * 3. PrivateRoute: Componente que protege las rutas privadas
 * 4. Pages: Componente que renderiza todas las rutas usando Switch y Router
 *
 * Ventajas de esta estructura:
 * - Rutas centralizadas: fácil de mantener y actualizar
 * - Reutilizable: Los datos se pueden usar en menús, navegación, etc.
 * - Dinámico: Agrega/quita rutas sin modificar componentes
 * - DRY (Don't Repeat Yourself): Define una vez, usa en varios lugares
 *
 * Uso:
 * - Para agregar nueva ruta: Agrega un objeto a PublicPages o PrivatePages
 * - Para acceder desde otros archivos: import { PublicPages, PrivatePages } from './routes/Pages'
 */

import { Switch } from 'react-router-dom';
import { Route } from 'react-router-dom';
import { Box } from '@mui/material';
import { useUser } from '../components/Auth/User';
import {
  AssignmentRounded,
  AttachMoneyRounded,
  BallotRounded,
  CardTravelRounded,
  EventAvailableRounded,
  ExitToAppRounded,
  FactCheckRounded,
  Groups2Rounded,
  HelpRounded,
  HomeRounded,
  InfoRounded,
  ListRounded,
  LocationCityRounded,
  ManageSearchRounded,
  PaidRounded,
  PasswordRounded,
  PersonRounded,
  RepeatRounded,
  SnippetFolderRounded,
  VideoCallRounded,
  WarningRounded,
} from '@mui/icons-material';

/* Components */
import Home from '../components/Screens/Home';
import Orden from '../components/Screens/Orden';
import Receta from '../components/Screens/Receta';
//import Prestador from '../components/Screens/Prestador';
import GrupoFamiliar from '../components/Screens/GrupoFamiliar';
import SignIn from '../components/Auth/SignIn';
import SignOut from '../components/Auth/SignOut';
import ValidarContacto from '../components/Contacto/ValidarContacto';
import EmitirOrden from '../components/Orden/EmitirOrden';
import ReimprimirOrden from '../components/Orden/ReimprimirOrden';
import BuscarPrestador from '../components/Prestador/BuscarPrestador';
import BuscarEstablecimiento from '../components/Prestador/BuscarEstablecimiento';
import BuscarFarmacias from '../components/Screens/Farmacias';
import ImprimirCuponCapita from '../components/Capitas/ImprimirCuponCapita';
import RecuperarPin from '../components/Contacto/RecuperarPin/RecuperarPin';
import OlvidePin from '../components/Contacto/RecuperarPin/OlvidePin';
import CambiarPassword from '../components/Auth/CambiarPassword';
import Perfil from '../components/Screens/Perfil';
import TerminosCondiciones from '../components/Screens/TerminosCondiciones';
import VerAfiliado from '../components/Afiliados/VerAfiliado';
import CrearCuenta from '../components/Afiliados/Cuenta/CrearCuenta';
import ConfirmarContactoView from '../components/Contacto/ConfirmarContactoView';
import ReadQr from '../components/Orden/GenerarOrdenDigital';
import ReadQrOrdenImpresa from '../components/Orden/ValidarOrdenImpresa';
import SmartQrReader from '../components/Orden/SmartQrReader';
import Mantenimiento from '../layouts/Messages/Page/Mantenimiento';
import ConfirmarContactoCrearCuentaView from '../components/Afiliados/Cuenta/ConfirmarContactoCrearCuenta';
import DocumentoPreview from '../components/Screens/DocumentoPreview';
import Prestadores from '../components/Screens/Prestadores';
import Ayuda from '../components/Screens/Ayuda';
import AddTransitoria from '../components/Derivaciones/AddTransitoria';
import Reintegros from '../components/Screens/Reintegros';
import Aportes from '../components/Screens/Aportes';
import Delegaciones from '../components/Screens/Delegaciones';
import Derivaciones from '../components/Screens/Derivaciones';
import ConsumoPrestacional from '../components/Screens/ConsumoPrestacional';
import VerAutorizaciones from '../components/Autorizaciones/VerAutorizaciones';
import BuscarRecetas from '../components/Receta/BuscarRecetas';
import BuscarRecetasCronicas from '../components/Receta/BuscarRecetasCronicas';
import ConsultarCupo from '../components/Orden/ConsultarCupo';
import ConsultarOrdenes from '../components/Orden/ConsultarOrdenes';
import { Redirect } from 'react-router-dom/cjs/react-router-dom';
import VerRecetaCronica from '../components/Receta/VerRecetaCronica';
import { VerAutorizacion } from '../components/Autorizaciones/VerAutorizacion';
import { Teleconsulta } from '../components/Screens/Teleconsulta';
import { TurnoPrevio } from '../components/Screens/TurnoPrevio';
import CredencialAfiliado from '../components/Screens/CredencialAfiliado';
import GenerarTokenFarmacia from '../components/Screens/GenerarTokenFarmacia';
import BuscarPrescripcionesElectronicas from '../components/PrescripcionElectronica/components/BuscarPrescripcionesElectronicas';
import { VerPrescripcionElectronica } from '../components/PrescripcionElectronica/components/VerPrescripcionElectronica';
import { OrdenAutomaticaPreview } from '../components/Orden/OrdenAutomaticaPreview';
import { Denuncias } from '../components/Screens/Denuncias';
import { AddDenuncia } from '../components/Denuncias/AddDenuncia';
import { ShowDenuncia } from '../components/Denuncias/ShowDenuncia';
import UploadTramitePage from '../components/SAT/pages/UploadTramitePage';
import BuscarReintegros from '../components/Reintegros/BuscarReintegros';
import { AddReintegro } from '../components/Reintegros/AddReintegro';
import { ShowReintegro } from '../components/Reintegros/ShowReintegro';
import { Reintegro } from '../components/Reintegros/Reintegro';
import { MenuMobilePage } from '../layouts/Header/MenuMobilePage';
import { NotificacionListMobile } from '../layouts/Header/components/NotificacionListMobile';
// import { LoginMobile } from '../components/Auth/LoginMobile';
import { ManualInstalacionIos } from '../@iosper/components/PWA/ManualInstalacionIos';
import { OlvidePinMobile } from '../components/Contacto/RecuperarPin/OlvidePinMobile';
import { CrearCuentaMobile } from '../components/Afiliados/Cuenta/CrearCuentaMobile';
import VisorOrdenExternaPage from '../components/Orden/pages/VisorOrdenExternaPage';
import CalificarAtencionPage from '../external/Apps/Turnero/CalificarAtencion/pages/CalificarAtencionPage';
import { Autorizaciones } from '../components/Screens/Autorizaciones';
import AddAutorizacion from '../components/Autorizaciones/AddAutorizacion';
import AdjuntarDocumentacionPage from '../components/Autorizaciones/AdjuntarDocumentacionPage';

// Public routes
/**
 * Objeto que define todas las rutas públicas (accesibles sin autenticación)
 * Cada propiedad contiene:
 * - to: ruta/path
 * - label: nombre descriptivo
 * - page: componente a renderizar
 * - exact: (opcional) si true, solo coincide exactamente con la ruta
 */
export const PublicPages = {
  Login: {
    to: '/signin',
    label: 'Login',
    page: <SignIn />,
  },
  // Login2: {
  //   to: '/login',
  //   label: 'Login',
  //   page: <Login />,
  // },
  RecuperarPIN: {
    to: '/recuperar-pin',
    label: 'Recuperar PIN',
    page: <RecuperarPin />,
  },
  OlvidePIN: {
    to: '/olvide-pin',
    label: 'Olvide PIN',
    page: <OlvidePin />,
  },
  OlvidePINMobile: {
    to: '/olvide-pin-mobile',
    label: 'Olvide PIN Mobile',
    page: <OlvidePinMobile />,
  },
  CrearCuenta: {
    to: '/crear-cuenta',
    label: 'Crear Cuenta',
    page: <CrearCuenta />,
    exact: true,
  },
  CrearCuentaMobile: {
    to: '/crear-cuenta-mobile',
    label: 'Crear Cuenta Mobile',
    page: <CrearCuentaMobile />,
    exact: true,
  },
  ValidarPIN: {
    to: '/validar-identidad/agregar-correo/recuperar-pin',
    label: 'Validar PIN',
    page: <CrearCuenta recuperarPin={true} />,
  },
  ConfirmarContacto: {
    to: '/confirmar-contacto-crear-cuenta',
    label: 'Confirmar Contacto',
    page: <ConfirmarContactoCrearCuentaView />,
  },
  Mantenimiento: {
    to: '/mantenimiento',
    label: 'Mantenimiento',
    page: <Mantenimiento />,
  },
  ValidarCredencial: {
    to: '/validar-credencial/:dataBase64',
    label: 'Validar Credencial',
    page: <CredencialAfiliado />,
  },
  GenerarTokenFarmacia: {
    to: '/generar-token-farmacia/',
    label: 'Generar Token Farmacia',
    page: <GenerarTokenFarmacia />,
  },
  UploadTramite: {
    to: '/upload-tramite',
    label: 'Upload Trámite',
    page: <UploadTramitePage />,
  },
  ManualInstalacionIos: {
    to: '/manual-instalacion-ios',
    label: 'Manual de Instalación iOS',
    page: <ManualInstalacionIos />,
  },
  VisorOrdenExterna: {
    to: '/orden/pdf/:token',
    label: 'Visor Orden Externa',
    exact: true,
    page: <VisorOrdenExternaPage />,
  },
  CalificarAtencionTurno: {
    to: '/turno/calificacion/:token',
    label: 'Calificar Atención de Turno',
    exact: true,
    page: <CalificarAtencionPage />,
  },
};

/* Private routes */
/**
 * Objeto que define todas las rutas privadas (requieren autenticación)
 * Cada propiedad contiene:
 * - to: ruta/path (puede incluir parámetros dinámicos como :id)
 * - label: nombre descriptivo de la página
 * - page: componente a renderizar
 * - exact: (opcional) si true, solo coincide exactamente con la ruta
 * - icon: (opcional) función que retorna un ícono para menús
 */
export const PrivatePages = {
  Inicio: {
    to: '/',
    label: 'Inicio',
    page: <Home />,
    exact: true,
    icon: <HomeRounded />,
  },
  Menu: {
    to: '/menu',
    label: 'Menú',
    page: <MenuMobilePage />,
    exact: true,
  },
  Perfil: {
    to: '/perfil',
    label: 'Perfil',
    page: <Perfil />,
    exact: true,
    icon: <PersonRounded />,
  },
  TerminosCondiciones: {
    to: '/terminos-condiciones',
    label: 'Términos y condiciones',
    page: <TerminosCondiciones />,
    exact: true,
    icon: <InfoRounded />,
  },
  SignOut: {
    to: '/signout',
    label: 'Salir',
    page: <SignOut />,
    icon: <ExitToAppRounded />,
  },
  ValidarContacto: {
    to: '/validar-contacto',
    label: 'Validar Contacto',
    page: <ValidarContacto />,
  },
  Orden: {
    to: '/orden',
    label: 'Ordenes',
    page: <Orden />,
    icon: <AssignmentRounded />,
  },
  EmitirOrden: {
    to: '/emitir-orden',
    label: 'Emitir Orden',
    page: <EmitirOrden />,
    exact: true,
  },
  EmitirOrdenAfiliado: {
    to: '/emitir-orden/:idAfiliado',
    label: 'Emitir Orden',
    page: <EmitirOrden />,
  },
  ReimprimirOrden: {
    to: '/reimprimir-orden',
    label: 'Reimprimir Orden',
    page: <ReimprimirOrden />,
  },
  VerOrdenAutomatica: {
    to: '/ver-orden-automatica/:idOrden',
    label: 'Ver Orden Automática',
    page: <OrdenAutomaticaPreview />,
  },
  GrupoFamiliar: {
    to: '/grupo-familiar',
    label: 'Grupo Familiar',
    page: <GrupoFamiliar />,
    icon: <Groups2Rounded />,
  },
  CuponPagoCapita: {
    to: '/cupon-pago-capita',
    label: 'Cápita',
    page: <ImprimirCuponCapita />,
    icon: <PaidRounded />,
  },
  CambiarPin: {
    to: '/cambiar-pin',
    label: 'Cambiar mi Contraseña',
    page: <CambiarPassword />,
    icon: <PasswordRounded fontSize='small' />,
  },
  VerAfiliado: {
    to: '/ver-afiliado/:idAfiliado',
    label: 'Ver Afiliado',
    page: <VerAfiliado />,
  },
  ValidarIdentidad: {
    to: '/validar-identidad/:validarIdentidad',
    label: 'Validar Identidad',
    page: <CrearCuenta />,
  },
  VerificarContacto: {
    to: '/verificar-contacto/:id/:contacto',
    label: 'Verificar Contacto',
    page: <ConfirmarContactoView />,
  },
  ReadQrSmart: {
    to: '/read-qr',
    label: 'Leer QR',
    page: <SmartQrReader />,
  },
  ReadOrden: {
    to: '/read-orden',
    label: 'Leer Orden',
    page: <ReadQr />,
  },
  ReadOrdenImpresa: {
    to: '/read-orden-impresa',
    label: 'Leer Orden Impresa',
    page: <ReadQrOrdenImpresa />,
  },
  Previsualizar: {
    to: '/previsualizar',
    label: 'Previsualizar',
    page: <DocumentoPreview />,
  },
  Prestadores: {
    to: '/cartilla',
    label: 'Cartilla',
    page: <Prestadores />,
    icon: <BallotRounded />,
  },
  BuscarPrestadores: {
    to: '/buscar-prestadores',
    label: 'Buscar Profesionales',
    page: <BuscarPrestador />,
  },
  BuscarEstablecimientos: {
    to: '/buscar-establecimientos',
    label: 'Buscar Establecimientos',
    page: <BuscarEstablecimiento />,
  },
  BuscarFarmacias: {
    to: '/buscar-farmacias',
    label: 'Buscar Farmacias',
    page: <BuscarFarmacias />,
  },
  Reintegros: {
    to: '/reintegros',
    label: 'Reintegros',
    page: <Reintegros />,
    exact: true,
    icon: <RepeatRounded />,
  },
  ReintegrosConsulta: {
    to: '/reintegros/consulta',
    label: 'Consulta Reintegros',
    page: <Reintegro />,
    exact: true,
  },
  ReintegrosBusqueda: {
    to: '/reintegros/busqueda',
    label: 'Buscar Reintegros',
    page: <BuscarReintegros />,
    exact: true,
  },
  ReintegrosGenerar: {
    to: '/reintegros/generar',
    label: 'Generar Reintegro',
    page: <AddReintegro />,
    exact: true,
    disabled: true,
  },
  ReintegrosVer: {
    to: '/reintegros/ver-reintegro',
    label: 'Ver Reintegro',
    page: <ShowReintegro />,
    exact: true,
  },
  Derivaciones: {
    to: '/derivaciones',
    label: 'Derivaciones',
    page: <Derivaciones />,
    icon: <CardTravelRounded />,
  },
  AddDerivacion: {
    to: '/add-derivacion',
    label: 'Agregar Derivación',
    page: <AddTransitoria />,
  },
  Aportes: {
    to: '/aportes',
    label: 'Aportes',
    page: <Aportes />,
    icon: <AttachMoneyRounded />,
  },
  ConsumoPrestacional: {
    to: '/consumo-prestacional',
    label: 'Consumo Prestacional',
    page: <ConsumoPrestacional />,
    icon: <ListRounded />,
  },
  Delegaciones: {
    to: '/delegaciones',
    label: 'Sedes',
    page: <Delegaciones />,
    icon: <LocationCityRounded />,
  },
  Autorizaciones: {
    to: '/autorizaciones',
    label: 'Autorizaciones',
    page: <Autorizaciones />,
    icon: <SnippetFolderRounded />,
  },
  VerAutorizacion: {
    to: '/ver-autorizacion',
    label: 'Ver Autorización',
    page: <VerAutorizacion />,
  },
  AgregarAutorizacion: {
    to: '/agregar-autorizacion',
    label: 'Agregar Autorización',
    page: <AddAutorizacion />,
  },
  AdjuntarDocumentacion: {
    to: '/adjuntar-documentacion',
    label: 'Adjuntar Documentación',
    page: <AdjuntarDocumentacionPage />,
  },
  Receta: {
    to: '/receta',
    label: 'Recetas Electrónicas',
    page: <Receta />,
    icon: <ManageSearchRounded />,
  },
  ConsultarRecetas: {
    to: '/consultar-recetas',
    label: 'Consultar Recetas',
    page: <BuscarRecetas />,
  },
  ConsultarRecetasCronicas: {
    to: '/consultar-recetas-cronicas',
    label: 'Consultar Recetas Crónicas',
    page: <BuscarRecetasCronicas />,
  },
  VerRecetaCronica: {
    to: '/ver-receta-cronica/:numeroReceta',
    label: 'Ver Receta Crónica',
    page: <VerRecetaCronica />,
  },
  ConsultarCupo: {
    to: '/consultar-cupo',
    label: 'Consultar Cupo',
    page: <ConsultarCupo />,
  },
  ConsultarOrdenes: {
    to: '/consultar-ordenes',
    label: 'Consultar Ordenes',
    page: <ConsultarOrdenes />,
  },
  Ayuda: {
    to: '/ayuda',
    label: 'Ayuda',
    page: <Ayuda />,
    icon: <HelpRounded />,
  },
  Teleconsulta: {
    to: '/teleconsulta',
    label: 'Teleconsulta',
    page: <Teleconsulta />,
    icon: <VideoCallRounded />,
  },
  TurnoPrevio: {
    to: '/turno-previo',
    label: 'Turno atención en Sede',
    page: <TurnoPrevio />,
    icon: <EventAvailableRounded />,
  },
  Prescripcion: {
    to: '/prescripcion',
    label: 'Prescripciones Electrónicas',
    page: <BuscarPrescripcionesElectronicas />,
    icon: <ManageSearchRounded />,
  },
  VerPrescripcion: {
    to: '/ver-prescripcion',
    label: 'Ver Prescripción',
    page: <VerPrescripcionElectronica />,
    exact: true,
  },
  VerPrescripcionId: {
    to: '/ver-prescripcion/:idPrescripcion',
    label: 'Ver Prescripción',
    page: <VerPrescripcionElectronica />,
  },
  Reclamo: {
    to: '/reclamo',
    label: 'Reclamos',
    page: <Denuncias />,
    exact: true,
    icon: <WarningRounded />,
    disable: true,
  },
  ReclamoNueva: {
    to: '/reclamo/nueva',
    label: 'Nuevo Reclamo',
    page: <AddDenuncia />,
    exact: true,
    disable: true,
  },
  VerReclamo: {
    to: '/reclamo/ver-reclamo',
    label: 'Ver Reclamo',
    page: <ShowDenuncia />,
    exact: true,
    disable: true,
  },
  Notificaciones: {
    to: '/notificaciones',
    label: 'Notificaciones',
    page: <NotificacionListMobile />,
  },
};

/**
 * Componente PrivateRoute - Protege rutas privadas
 *
 * Verifica si el usuario está autenticado antes de permitir el acceso.
 * Si el usuario NO está autenticado, lo redirige a /signin
 *
 * @param {ReactNode} children - Componente a renderizar si es autenticado
 * @param {object} props - Props adicionales pasados a <Route />
 * @returns {ReactNode} Renderiza children si user existe, sino redirige a login
 */
const PrivateRoute = ({ children, ...props }) => {
  const { user } = useUser();
  return (
    <Route
      {...props}
      render={({ location }) => {
        return user ? (
          children
        ) : (
          <Redirect to={{ pathname: '/signin', state: { from: location } }} />
        );
      }}
    />
  );
};

/**
 * Componente Pages - Gestor principal de rutas
 *
 * Renderiza todas las rutas de la aplicación (públicas y privadas)
 * Utiliza Object.values() para iterar dinámicamente sobre los objetos PublicPages y PrivatePages
 * Esto permite mantener las rutas centralizadas y reutilizables
 *
 * Estructura:
 * - Rutas públicas: accesibles sin autenticación
 * - Rutas privadas: protegidas con verificación de usuario
 *
 * @returns {ReactNode} Switch con todas las rutas configuradas
 */
const Pages = () => (
  <Box>
    <Switch>
      {/* TODO Error/Services pages */}

      {/* Rutas públicas - Iteralas dinámicamente desde PublicPages */}
      {Object.values(PublicPages).map(({ to, page, exact, disable = false }, key) => (
        !disable && <Route key={key} path={to} exact={exact || false}>
          {page}
        </Route>
      ))}

      {/* Rutas privadas - Protegidas con PrivateRoute + iteradas desde PrivatePages */}
      {Object.values(PrivatePages).map(({ to, page, exact, disable = false }, key) => (
        !disable && <PrivateRoute key={key} path={to} exact={exact || false}>
          {page}
        </PrivateRoute>
      ))}
    </Switch>
  </Box>
);

export default Pages;
